import { ApolloCache, Reference } from '@apollo/client';
import { NormalizedCacheObject } from '@apollo/client/cache';

import {
    IModuleFragment,
    IEmptyModuleGroupFragment,
    IModuleWeight,
    IModuleType,
    ITrainingMatrixModuleFragment,
    IConditionEffect,
    TrainingModuleGroupsFragmentDoc,
} from 'graphql/types';

function isReference(value: unknown): value is Reference {
    return (
        typeof value === 'object' &&
        value !== null &&
        '__ref' in value &&
        typeof (value as Reference).__ref === 'string'
    );
}

/**
 * Evict MeetingModule cache entries that reference any of the given locations.
 * Apollo filters dangling refs from arrays on read, so parent moduleGroups
 * will naturally exclude the evicted modules. Callers should follow up with
 * cache.gc() to clean up the now-unreferenced Location entries.
 */
export function evictMeetingModulesByLocationIds(
    cache: ApolloCache,
    locationIds: string[]
) {
    if (!locationIds.length) return;

    const deletedRefs = new Set(locationIds.map((id) => `Location:${id}`));
    const cacheData = cache.extract() as NormalizedCacheObject;

    for (const [cacheKey, entity] of Object.entries(cacheData)) {
        if (!entity || entity.__typename !== 'MeetingModule') continue;

        const locationRef = entity.location;

        if (isReference(locationRef) && deletedRefs.has(locationRef.__ref)) {
            cache.evict({ id: cacheKey });
        }
    }
}

export function createModuleInCache(
    newModule: IModuleFragment,
    moduleGroup: IEmptyModuleGroupFragment,
    isNewModuleGroup: boolean,
    trainingId: string,
    cache: ApolloCache
) {
    // If this is a new module group we want to add the group to the training
    if (isNewModuleGroup) {
        const newModuleGroupData = {
            __typename: 'ModuleGroup' as const,
            id: moduleGroup.id,
            title: '',
            description: '',
            conditions: [],
            startDate: null,
            endDate: null,
            deadline: null,
            conditionEffect: IConditionEffect.Nothing,
            reminder: null,
            modules: [newModule],
        };

        // Update the training's moduleGroups using updateFragment
        cache.updateFragment(
            {
                id: `Training:${trainingId}`,
                fragment: TrainingModuleGroupsFragmentDoc,
                fragmentName: 'TrainingModuleGroups',
            },
            (data) => {
                if (!data) return data;

                return {
                    ...data,
                    moduleGroups: [
                        ...(data.moduleGroups || []),
                        newModuleGroupData,
                    ],
                };
            }
        );

        return;
    }

    // For existing module groups, add the new module to the modules array
    cache.modify({
        id: cache.identify(moduleGroup),
        fields: {
            modules(cachedModuleRefs, { toReference }) {
                const existing = Array.isArray(cachedModuleRefs)
                    ? cachedModuleRefs
                    : [];

                return [...existing, toReference(newModule)];
            },
        },
    });
}

/**
 *  Return the actual weight value of the enum
 *
 * @param weight    Module weight value of enum
 * @returns number  The actual weight value
 */
export function getModuleWeightValue(weight: IModuleWeight): number {
    return Object.values(IModuleWeight).indexOf(weight);
}

/**
 *
 * @param module
 * @returns string  The type of the module
 */
export function getModuleType(module: IModuleFragment): IModuleType {
    switch (module.__typename) {
        case 'AssignmentModule':
            return IModuleType.Assignment;
        case 'CertificateModule':
            return IModuleType.Certificate;
        case 'FileModule':
            return IModuleType.File;
        case 'LinkModule':
            return IModuleType.Link;
        case 'ExternalContentModule':
            return IModuleType.ExternalContent;
        case 'ArticleModule':
            return IModuleType.Article;
        case 'CollectionModule':
            return IModuleType.Collection;
        case 'LTIModule':
            return IModuleType.Lti;
        case 'VideoModule':
            return IModuleType.Video;
        case 'MeetingModule':
            return IModuleType.Meeting;
        case 'CourseModule':
            return IModuleType.Course;
        case 'ScormModule':
            return IModuleType.Scorm;
        case 'AfasWebhookModule':
            return IModuleType.AFAS;
        case 'WebhookModule':
            return IModuleType.Webhook;
    }
}

/**
 * Get Module title
 *
 * @param module ITrainingMatrixModuleFragment | IModuleFragment
 *
 * @returns string
 */
export const getModuleTitle = (
    module: ITrainingMatrixModuleFragment | IModuleFragment
) => {
    if ('title' in module && module.title) {
        return module.title;
    }

    if ('course' in module && module.course) {
        return module.course.title;
    }

    if ('scorm' in module && module.scorm) {
        return module.scorm.title;
    }

    if ('content' in module && module.content) {
        return module.content.title;
    }

    return '';
};
